import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, MagnifyingGlass, SignOut, User, Plus, List, Tag, Gift, ChatDots, ShoppingBag, Clock, Storefront, Heart, GearSix, Handshake } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function Navbar() {
  const { user, logout } = useAuth();

  return (
    <nav className="navbar" data-testid="navbar">
      <Link to={user ? "/explore" : "/"} className="navbar-brand" data-testid="navbar-brand">
        <MapPin size={28} weight="fill" color="#FFD166" />
        SaleSeeker
      </Link>

      <div className="flex items-center gap-3">
        {user ? (
          <>
            <Link to="/explore" className="text-white hover:text-[#FFD166] transition-colors hidden sm:flex items-center gap-1 text-sm font-semibold" data-testid="nav-explore">
              <MagnifyingGlass size={18} weight="bold" />
              Explore
            </Link>
            <Link to="/shop" className="text-white hover:text-[#FFD166] transition-colors hidden sm:flex items-center gap-1 text-sm font-semibold" data-testid="nav-shop">
              <ShoppingBag size={18} weight="bold" />
              Shop
            </Link>
            <Link to="/create" data-testid="nav-create-sale">
              <button className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
                <Plus size={16} weight="bold" />
                Post Sale
              </button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-full bg-[#FFD166] border-2 border-white flex items-center justify-center text-[#004E89] font-bold text-sm cursor-pointer" data-testid="user-menu-trigger">
                  {user.name?.[0]?.toUpperCase() || 'U'}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] rounded-xl">
                <div className="px-3 py-2 text-sm font-bold text-[#004E89]">{user.name}</div>
                <div className="px-3 pb-2 text-xs text-[#5C7A99]">{user.email}</div>
                {user.subscription === 'premium' && (
                  <div className="px-3 pb-2">
                    <span className="tag tag-estate text-[10px]">PRO</span>
                  </div>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/my-listings" className="flex items-center gap-2 cursor-pointer" data-testid="nav-my-listings">
                    <List size={16} /> My Listings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/saved" className="flex items-center gap-2 cursor-pointer" data-testid="nav-saved-sales">
                    <Heart size={16} /> Saved Sales
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/past-sales" className="flex items-center gap-2 cursor-pointer" data-testid="nav-past-sales">
                    <Clock size={16} /> Past Sales
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/hire" className="flex items-center gap-2 cursor-pointer" data-testid="nav-hire-agent">
                    <Handshake size={16} /> Hire an Agent
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/shop" className="flex items-center gap-2 cursor-pointer" data-testid="nav-shop-menu">
                    <ShoppingBag size={16} /> Shop
                  </Link>
                </DropdownMenuItem>
                {user.role === 'admin' && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer font-bold text-[#FF6B35]" data-testid="nav-admin-dashboard">
                        <GearSix size={16} weight="bold" /> Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/post-merchandise" className="flex items-center gap-2 cursor-pointer" data-testid="nav-post-merch">
                        <Storefront size={16} /> Post Merch
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/pricing" className="flex items-center gap-2 cursor-pointer" data-testid="nav-pricing">
                    <Tag size={16} /> Pricing
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/referral" className="flex items-center gap-2 cursor-pointer" data-testid="nav-referral">
                    <Gift size={16} /> Refer &amp; Earn
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/contact" className="flex items-center gap-2 cursor-pointer" data-testid="nav-contact">
                    <ChatDots size={16} /> Contact
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="flex items-center gap-2 cursor-pointer text-red-600" data-testid="nav-logout">
                  <SignOut size={16} /> Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <Link to="/shop" className="text-white hover:text-[#FFD166] transition-colors font-semibold text-sm hidden sm:block" data-testid="nav-shop-public">
              Shop
            </Link>
            <Link to="/hire" className="text-white hover:text-[#FFD166] transition-colors font-semibold text-sm hidden sm:block" data-testid="nav-hire-public">
              Hire an Agent
            </Link>
            <Link to="/contact" className="text-white hover:text-[#FFD166] transition-colors font-semibold text-sm hidden sm:block" data-testid="nav-contact-public">
              Contact
            </Link>
            <Link to="/login" data-testid="nav-login">
              <button className="text-white hover:text-[#FFD166] transition-colors font-semibold text-sm">
                Log In
              </button>
            </Link>
            <Link to="/register" data-testid="nav-register">
              <button className="btn-secondary text-sm py-2 px-4">
                Sign Up
              </button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}
